import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import {
  fetchOrganizationUsers,
  fetchOrganizations,
  moveUsers,
  fetchAllUsers,
  fetchUnassignedUsers,
  fetchPendingUsers,
  handlePendingUsers,
} from "./actions";
import { RootState } from "Redux/store";
import { Organization, User } from "./types";

export interface AdminState {
  organizations: Organization[];
  users: User[];
  selectedUsers: User[];
  selectedUnassignedUsers: User[];
  checked: boolean;
  indeterminate: boolean;
  loadingOrganizations: boolean;
  loadingUsers: boolean;
  loadingAllUsers: boolean;
  allUsers: User[];
  loadingUnassignedUsers: boolean;
  unassingedUsers: User[];
  loadingPendingUsers: boolean;
  pendingUsers: User[];
}

const initialState: AdminState = {
  organizations: [],
  users: [],
  selectedUsers: [],
  selectedUnassignedUsers: [],
  checked: false,
  indeterminate: false,
  loadingUsers: false,
  loadingOrganizations: false,
  loadingAllUsers: false,
  allUsers: [],
  loadingUnassignedUsers: false,
  unassingedUsers: [],
  loadingPendingUsers: false,
  pendingUsers: [],
};

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    toggleAll: (state) => {
      const { checked, indeterminate, users } = state;
      const newSelectedUsers = checked || indeterminate ? [] : users;
      state.selectedUsers = newSelectedUsers;
      state.checked = !checked && !indeterminate;
      state.indeterminate = false;
    },
    toggleUserSelection: (
      state,
      action: PayloadAction<{ user: User; checked: boolean }>
    ) => {
      const { user, checked } = action.payload;
      const { selectedUsers } = state;
      state.selectedUsers = checked
        ? [...selectedUsers, user]
        : selectedUsers.filter((p) => p.user_id !== user.user_id);
    },
    toggleAllUnassignedUsers: (state) => {
      const { checked, indeterminate, unassingedUsers } = state;
      const newSelectedUsers = checked || indeterminate ? [] : unassingedUsers;
      state.selectedUnassignedUsers = newSelectedUsers;
      state.checked = !checked && !indeterminate;
      state.indeterminate = false;
    },
    toggleUnassignedUserSelection: (
      state,
      action: PayloadAction<{ user: User; checked: boolean }>
    ) => {
      const { user, checked } = action.payload;
      const { selectedUnassignedUsers } = state;
      state.selectedUnassignedUsers = checked
        ? [...selectedUnassignedUsers, user]
        : selectedUnassignedUsers.filter((p) => p.user_id !== user.user_id);
    },
    resetSelectedUsers: (state) => {
      state.selectedUnassignedUsers = [];
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchOrganizations.pending, (state) => {
        state.loadingOrganizations = true;
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        state.organizations = action.payload.data;
        state.loadingOrganizations = false;
      })
      .addCase(fetchOrganizationUsers.pending, (state) => {
        state.users = [];
        state.loadingUsers = true;
      })
      .addCase(fetchOrganizationUsers.fulfilled, (state, action) => {
        state.users = action.payload.data;
        state.loadingUsers = false;
      })
      .addCase(moveUsers.fulfilled, (state) => {
        if (state.selectedUnassignedUsers.length > 0) {
          const selectedUserIds = state.selectedUnassignedUsers.map(
            (user) => user.user_id
          );
          state.unassingedUsers = state.unassingedUsers.filter(
            (user) => !selectedUserIds.includes(user.user_id)
          );
          state.selectedUnassignedUsers = [];
        }
        const selectedUserIds = state.selectedUsers.map((user) => user.user_id);
        state.users = state.users.filter(
          (user) => !selectedUserIds.includes(user.user_id)
        );
        state.selectedUsers = [];
      })
      .addCase(fetchAllUsers.pending, (state) => {
        state.loadingAllUsers = true;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.allUsers = action.payload;
        state.loadingAllUsers = false;
      })
      .addCase(fetchUnassignedUsers.pending, (state) => {
        state.loadingUnassignedUsers = true;
      })
      .addCase(fetchUnassignedUsers.fulfilled, (state, action) => {
        state.unassingedUsers = action.payload;
        state.loadingUnassignedUsers = false;
      })
      .addCase(fetchPendingUsers.pending, (state) => {
        state.loadingPendingUsers = true;
      })
      .addCase(fetchPendingUsers.fulfilled, (state, action) => {
        state.pendingUsers = action.payload;
        state.loadingPendingUsers = false;
      })
      .addCase(handlePendingUsers.fulfilled, (state, action) => {
        const selectedPendingInvitationId =
          action.payload.data.user_invitation_id;
        state.pendingUsers = state.pendingUsers.filter(
          (user) =>
            !selectedPendingInvitationId.includes(user.user_invitation_id)
        );
      }),
});

export const {
  toggleAll,
  toggleUserSelection,
  toggleUnassignedUserSelection,
  toggleAllUnassignedUsers,
  resetSelectedUsers,
} = adminSlice.actions;

export default adminSlice.reducer;

export const selectOrganizations = (state: RootState) =>
  state.admin.organizations;

export const selectAllOtherOrganizations = createSelector(
  selectOrganizations,
  (_: RootState, currentOrganizationId: string) => currentOrganizationId,
  (organizations, currentOrganizationId) =>
    organizations.filter((org) => org.organisation_id !== currentOrganizationId)
);

export const selectOrganizationById = createSelector(
  selectOrganizations,
  (_: RootState, id: string) => id,
  (organizations, id) => organizations.find((org) => org.organisation_id === id)
);

export const selectUsers = (state: RootState) => state.admin.users;

export const selectSelectedUsers = (state: RootState) =>
  state.admin.selectedUsers;

export const selectUnassignedSelectedUsers = (state: RootState) =>
  state.admin.selectedUnassignedUsers;

export const selectChecked = (state: RootState) => state.admin.checked;

export const selectLoadingUsers = (state: RootState) =>
  state.admin.loadingUsers;

export const selectLoadingOrganizations = (state: RootState) =>
  state.admin.loadingOrganizations;

export const selectAllUsers = (state: RootState) => state.admin.allUsers;
export const selectLoadingAllUsers = (state: RootState) =>
  state.admin.loadingAllUsers;

export const selectUnassignedUsers = (state: RootState) =>
  state.admin.unassingedUsers;

export const selectLoadingUnassignedUser = (state: RootState) =>
  state.admin.loadingUnassignedUsers;

export const selectPendingUsers = (state: RootState) =>
  state.admin.pendingUsers;

export const selectLoadingPendingUsers = (state: RootState) =>
  state.admin.loadingPendingUsers;
